import { BellRing, Volume2, VolumeX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { PageHeader } from "@/components/PageHeader";
import { CoachMarks } from "@/features/help/CoachMarks";
import { FirstRunChecklist } from "@/features/help/FirstRunChecklist";
import { usePermissions } from "@/lib/auth/usePermissions";
import { useLiveInvalidate } from "@/realtime/hooks";

import { BigActions } from "./BigActions";
import { KpiCards } from "./KpiCards";
import { RecentActivity } from "./RecentActivity";
import { TodoInbox } from "./TodoInbox";
import { useNewOrders } from "./useNewOrders";
import { useTodoSources } from "./useTodoSources";

/**
 * Home (T11-10, design/04 §0/§2): the four big buttons, "what needs doing today" and the numbers
 * of today / 7 / 30 days (web and shop register only — no marketplace in phase 1, R18). Each part
 * loads and fails on its own; everything refreshes every 60 s while the tab is open, new orders
 * at once through realtime.
 */
export function DashboardPage() {
  const { t } = useTranslation(["common", "dashboard"]);
  const perms = usePermissions();
  const sources = useTodoSources();
  const newOrders = useNewOrders(sources.ordersToday);
  // R21 (T11-52): the other to-do counters (conflicts, consignment, stock, tax) count themselves
  useLiveInvalidate("staff:inventory", [["dashboard"]], { pollMs: 60_000 });
  useLiveInvalidate("staff:consignment", [["dashboard"]], { pollMs: 60_000 });
  useLiveInvalidate("staff:catalog", [["dashboard"]], {
    match: (m) => m.entity === "product",
    pollMs: 60_000,
  });
  useLiveInvalidate("staff:tax", [["dashboard"]], { pollMs: 60_000 });

  const badge =
    newOrders.count > 0 ? (
      <span
        data-slot="new-orders-badge"
        className="absolute top-2 right-2 rounded-full bg-[#b91c1c] px-2 py-0.5 text-xs font-bold text-white"
      >
        {t("dashboard:new_orders", { count: newOrders.count })}
      </span>
    ) : null;

  return (
    <div className="flex flex-col gap-8" data-page="home">
      <PageHeader
        title={t("home.title")}
        secondaryActions={
          perms.can("orders.read") ? (
            <button
              type="button"
              onClick={() => {
                newOrders.setSound(!newOrders.sound);
              }}
              aria-pressed={newOrders.sound}
              className="inline-flex min-h-touch items-center gap-2 rounded-md border px-3 text-sm hover:bg-accent"
            >
              {newOrders.sound ? (
                <Volume2 className="size-4" aria-hidden />
              ) : (
                <VolumeX className="size-4" aria-hidden />
              )}
              {t("dashboard:sound")}
            </button>
          ) : undefined
        }
      />
      {newOrders.count > 0 ? (
        <div
          role="status"
          data-slot="new-orders"
          className="flex flex-wrap items-center gap-3 rounded-md border-2 border-[#b91c1c] bg-[#fef2f2] p-3 text-[#991b1b]"
        >
          <BellRing className="size-5" aria-hidden />
          <span className="flex-1 font-semibold">
            {t("dashboard:new_orders_banner", { count: newOrders.count })}
          </span>
          <Link
            to="/orders"
            onClick={newOrders.reset}
            className="inline-flex min-h-touch items-center rounded-md bg-[#b91c1c] px-4 font-semibold text-white"
          >
            {t("dashboard:see_orders")}
          </Link>
        </div>
      ) : null}
      <BigActions badges={{ view_orders: badge }} />
      {/* T11-47: "Get your shop ready" (owner, until hidden) + the first-visit tour */}
      <FirstRunChecklist />
      <CoachMarks />
      <div className="grid gap-8 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <TodoInbox sources={sources} />
        <div className="flex flex-col gap-8">
          <KpiCards />
          <RecentActivity />
        </div>
      </div>
    </div>
  );
}
